import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, MessageCircle, Palette } from "lucide-react";
import { cn } from "@/lib/utils";
import { CRAFT, SITE, whatsappLink } from "@/lib/site";
import { fetchPortfolio, type PortfolioItem } from "@/lib/content-client";
import { photoForUrl, srcset, type Photo } from "@/lib/photos";
import {
  galleryGap,
  justify,
  targetRowHeight,
  useContainerWidth,
  type Placed,
} from "@/lib/justified";
import { SiteHeader } from "@/components/SiteHeader";
import { Reveal } from "@/components/Reveal";
import { SplitText } from "@/components/SplitText";
import { PortfolioLightbox } from "@/components/PortfolioLightbox";
import { Marquee } from "@/components/Marquee";
import "./index.css";

const VOCABULARY = [
  "Layering",
  "Blending",
  "Pigment",
  "Precision",
  "Undertone",
  "Light",
  "Texture",
  "Balance",
  "Symmetry",
  "Finish",
];

/**
 * The shape to lay an image out at.
 *
 * Stored dimensions win. Failing those, a row pointing at the curated work
 * resolves back to the photo manifest, so older rows still get their true
 * ratio. Only when neither is available does this guess — a 4:5 portrait, the
 * right default for this kind of work, which beats a square.
 */
const FALLBACK_RATIO = 4 / 5;

function photoFor(item: PortfolioItem): Photo | null {
  return photoForUrl(item.src);
}

function ratioFor(item: PortfolioItem) {
  if (item.width && item.height) return item.width / item.height;
  const photo = photoFor(item);
  if (photo) return photo.width / photo.height;
  return FALLBACK_RATIO;
}

/** One image, drawn at its own shape. */
function Tile({
  item,
  spot,
  onOpen,
}: {
  item: PortfolioItem;
  spot: Placed;
  onOpen: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const photo = photoFor(item);
  // Manifest-backed rows load the same four widths as the rest of the site;
  // R2 uploads have no entry and load the single file.
  const responsive = photo ? srcset(photo) : "";
  const { box, draw, matted } = spot;

  // A band — a panorama, a screenshot — is too shallow to carry a caption, so
  // it is left as a clean strip and the lightbox carries the title instead.
  const band = box.height < 56;

  return (
    // The cell is the positioned element and the image sits inside it at the
    // drawn rectangle. That keeps the mat a real, visible part of the tile
    // rather than bare page showing through a gap.
    <div
      role="listitem"
      className="absolute"
      style={{ left: box.x, top: box.y, width: box.width, height: box.height }}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={item.alt || item.title}
        className={cn(
          "group relative block h-full w-full cursor-zoom-in overflow-hidden ring-1 transition-[box-shadow,transform] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] hover:z-10 hover:-translate-y-0.5 hover:shadow-[0_24px_60px_-24px_rgba(0,0,0,0.85)] hover:ring-gold/50",
          matted ? "bg-espresso/70 ring-ivory/[0.07]" : "bg-espresso/45 ring-ivory/10",
        )}
      >
        {/* Blurred placeholder from the manifest, so a tile is never an empty
            rectangle while the image loads. */}
        {photo && (
          <span
            aria-hidden="true"
            className="absolute inset-0 scale-110 bg-cover bg-center blur-xl transition-opacity duration-700"
            style={{
              backgroundImage: `url(${photo.lqip})`,
              opacity: loaded && !failed ? 0 : 1,
            }}
          />
        )}

        <img
          src={item.src}
          {...(responsive ? { srcSet: responsive, sizes: `${draw.width}px` } : {})}
          alt={item.alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          // A missing file resolves to the SPA shell, which fails to decode. The
          // tile then keeps its mat rather than showing a broken-image glyph — a
          // visible gap in the wall is better than a visible error.
          className={cn(
            "absolute object-cover transition-opacity duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]",
            loaded && !failed ? "opacity-100" : "opacity-0",
          )}
          style={{
            left: draw.x - box.x,
            top: draw.y - box.y,
            width: draw.width,
            height: draw.height,
          }}
        />

        {/* Caption. Always present on touch, where there is no hover to reveal
            it; revealed on hover for pointer devices. */}
        {!band && (
          <span
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-espresso/90 via-espresso/25 to-transparent p-3 transition-opacity duration-500 sm:p-4",
              "opacity-100 md:opacity-0 md:group-hover:opacity-100",
            )}
          >
            {item.category && (
              <span className="font-sans text-[0.5rem] tracking-[0.24em] text-gold uppercase sm:text-[0.5625rem]">
                {item.category}
              </span>
            )}
            <span className="mt-1 font-display text-sm leading-snug font-light text-ivory sm:text-lg">
              {item.title}
            </span>
            <span className="mt-2 hidden h-px w-full origin-left scale-x-0 bg-gold/70 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100 sm:block" />
          </span>
        )}
      </button>
    </div>
  );
}

/**
 * The gallery.
 *
 * Rows are justified to the container width, so every image is shown at its own
 * aspect ratio — nothing is cropped to fit a fixed tile, which is what makes a
 * grid of mixed portrait and landscape work look deliberate rather than
 * assembled. Width is measured rather than assumed, so a phone, a resized
 * window and an embedded frame each get their own layout.
 */
function JustifiedGallery({
  items,
  onOpen,
}: {
  items: PortfolioItem[];
  onOpen: (index: number) => void;
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const gap = galleryGap(width);
  const { placed, height } =
    width > 0
      ? justify(items, ratioFor, width, targetRowHeight(width), gap)
      : { placed: [], height: 0 };

  return (
    // The measured element is always mounted, skeleton included: it is what the
    // width is read from, so rendering it conditionally would leave the first
    // measurement permanently at zero.
    <div ref={ref} className="w-full">
      {width <= 0 ? (
        <div className="space-y-2.5 sm:space-y-3" aria-hidden="true">
          {[190, 150, 210].map(h => (
            <div
              key={h}
              className="animate-pulse rounded-sm bg-ivory/[0.06]"
              style={{ height: h }}
            />
          ))}
        </div>
      ) : (
        <div className="relative w-full" style={{ height }} role="list">
          {placed.map((spot, i) => (
            <Tile
              key={items[i]?.id ?? i}
              item={items[i]!}
              spot={spot}
              onOpen={() => onOpen(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function PortfolioPage() {
  const [items, setItems] = useState<PortfolioItem[] | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [active, setActive] = useState<string>("All");
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    fetchPortfolio().then(data => {
      if (!live) return;
      if (data.ok) {
        setItems(data.items);
        setCategories(data.categories);
      } else {
        setFailed(true);
        setItems([]);
      }
    });
    return () => {
      live = false;
    };
  }, []);

  // Close the viewer if the filter narrows the list out from under it.
  useEffect(() => {
    setOpen(null);
  }, [active]);

  useEffect(() => {
    document.title = `The Art — ${SITE.name}`;
  }, []);

  const visible = useMemo(
    () => (items ?? []).filter(i => active === "All" || i.category === active),
    [items, active],
  );

  const loading = items === null;

  return (
    <div className="grain min-h-screen bg-espresso text-ivory">
      <SiteHeader tone="dark" />

      <main>
        {/* ------------------------------------------------------------ hero */}
        <section className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
          <div aria-hidden="true" className="hatch pointer-events-none absolute inset-0 opacity-[0.05]" />
          <div
            aria-hidden="true"
            className="animate-float-soft pointer-events-none absolute -top-32 left-1/2 h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-gold/12 blur-[120px]"
          />

          <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
            <Reveal variant="fade">
              <a
                href="/"
                className="group inline-flex items-center gap-2 font-sans text-[0.625rem] tracking-[0.24em] text-ivory/50 uppercase transition-colors hover:text-gold"
              >
                <ArrowLeft
                  className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-x-1"
                  strokeWidth={1.5}
                />
                Back to the studio
              </a>
            </Reveal>

            <Reveal variant="fade" delay={80}>
              <p className="mt-10 font-sans text-[0.6875rem] font-medium tracking-[0.32em] text-gold uppercase">
                The Portfolio
              </p>
            </Reveal>

            <SplitText
              as="h1"
              text="Every face I have painted."
              className="mt-6 max-w-4xl font-display text-[clamp(2.5rem,7.5vw,5.5rem)] leading-[1.02] font-light"
              stagger={55}
            />

            <Reveal variant="up" delay={200}>
              <p className="mt-8 max-w-xl font-sans text-sm leading-relaxed font-light text-ivory/70 sm:text-base">
                Past work, kept in the order I would hang it. Tap any piece to see it full
                size — the finish only makes sense at the distance it will actually be
                seen.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ---------------------------------------------------------- filters */}
        {(categories.length > 0 || loading) && (
          <div className="sticky top-[4.5rem] z-30 border-y border-ivory/10 bg-espresso/90 backdrop-blur-md">
            <div className="mx-auto max-w-6xl px-6 lg:px-10">
              <div className="no-scrollbar flex items-center gap-3 overflow-x-auto py-4">
                {["All", ...categories].map(label => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setActive(label)}
                    aria-pressed={active === label}
                    className={cn(
                      "shrink-0 cursor-pointer rounded-full border px-4 py-2 font-sans text-[0.625rem] tracking-[0.2em] uppercase transition-all duration-400",
                      active === label
                        ? "border-gold bg-gold text-espresso"
                        : "border-ivory/20 text-ivory/65 hover:border-gold/60 hover:text-gold",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ----------------------------------------------------------- gallery */}
        <section className="mx-auto max-w-6xl px-6 py-16 sm:py-24 lg:px-10">
          {loading && (
            <div className="space-y-2.5 sm:space-y-3" aria-hidden="true">
              {[0.62, 0.78, 0.5].map((fraction, row) => (
                <div
                  key={row}
                  className="animate-pulse rounded-sm bg-ivory/[0.06]"
                  style={{ height: `min(${Math.round(fraction * 100)}%, 240px)` }}
                />
              ))}
            </div>
          )}

          {!loading && failed && (
            <p className="py-20 text-center font-sans text-sm font-light text-ivory/60">
              The gallery could not be loaded. Please try again shortly.
            </p>
          )}

          {!loading && !failed && visible.length === 0 && (
            <div className="flex flex-col items-center py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-ivory/20 text-gold/70">
                <Palette className="h-6 w-6" strokeWidth={1.25} />
              </span>
              <h2 className="mt-7 font-display text-2xl font-light">
                {items && items.length > 0 ? "Nothing in this category yet" : "The wall is still empty"}
              </h2>
              <p className="mt-3 max-w-sm font-sans text-sm leading-relaxed font-light text-ivory/55">
                {items && items.length > 0
                  ? "Try another category, or view everything."
                  : "New pieces are added here as the work is finished. Check back soon."}
              </p>
              {items && items.length > 0 && (
                <button
                  type="button"
                  onClick={() => setActive("All")}
                  className="mt-8 cursor-pointer rounded-full border border-gold px-7 py-3 font-sans text-[0.625rem] tracking-[0.22em] text-gold uppercase transition-colors duration-400 hover:bg-gold hover:text-espresso"
                >
                  View everything
                </button>
              )}
            </div>
          )}

          {!loading && visible.length > 0 && (
            <JustifiedGallery items={visible} onOpen={setOpen} />
          )}

        </section>

        {/* ------------------------------------------------------------ craft */}
        <section className="border-t border-ivory/10 py-20 sm:py-28">
          <div className="mx-auto max-w-6xl px-6 lg:px-10">
            <Reveal variant="fade">
              <p className="font-sans text-[0.6875rem] font-medium tracking-[0.32em] text-gold uppercase">
                How it is made
              </p>
            </Reveal>
            <div className="mt-12 grid gap-x-16 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
              {CRAFT.map((movement, i) => (
                <Reveal key={movement.title} variant="up" delay={i * 90}>
                  <article className="border-t border-ivory/15 pt-7">
                    <span
                      aria-hidden="true"
                      className="font-display text-[3.5rem] leading-none text-ivory/10"
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="mt-3 font-sans text-[0.5625rem] tracking-[0.26em] text-gold uppercase">
                      {movement.medium}
                    </p>
                    <h3 className="mt-2 font-display text-xl leading-snug font-light">
                      {movement.title}
                    </h3>
                    <p className="mt-3 font-sans text-sm leading-relaxed font-light text-ivory/60">
                      {movement.body}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* -------------------------------------------------------------- cta */}
        <section className="border-t border-ivory/10 py-20 text-center sm:py-28">
          <div className="mx-auto max-w-2xl px-6">
            <Reveal variant="up">
              <h2 className="font-display text-[clamp(2rem,5vw,3.25rem)] leading-[1.08] font-light">
                Want this finish on your face?
              </h2>
              <p className="mx-auto mt-5 max-w-md font-sans text-sm leading-relaxed font-light text-ivory/60">
                Tell me the occasion and the date. I&rsquo;ll come back with availability and
                a plan for your look.
              </p>
              <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-9 py-4 font-sans text-[0.6875rem] tracking-[0.24em] text-espresso uppercase transition-transform duration-500 hover:-translate-y-0.5 sm:w-auto"
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                  Message on WhatsApp
                </a>
                <a
                  href="/#contact"
                  className="inline-flex w-full items-center justify-center rounded-full border border-ivory/25 px-9 py-4 font-sans text-[0.6875rem] tracking-[0.24em] uppercase transition-colors duration-500 hover:border-gold hover:text-gold sm:w-auto"
                >
                  Send an enquiry
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        <div className="border-t border-ivory/10 py-7">
          <Marquee
            duration={34}
            reverse
            className="font-display text-2xl text-ivory/30 sm:text-3xl"
            itemClassName="items-center"
            gap="2.5rem"
          >
            {VOCABULARY.map(word => (
              <span key={word} className="whitespace-nowrap">
                {word}
                <span className="ml-10 text-gold/50">·</span>
              </span>
            ))}
          </Marquee>
        </div>
      </main>

      <footer className="border-t border-ivory/10 px-6 py-10 lg:px-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="font-display text-base tracking-[0.2em] uppercase">{SITE.name}</p>
          <p className="font-sans text-[0.625rem] tracking-[0.2em] text-ivory/40 uppercase">
            &copy; {new Date().getFullYear()} {SITE.name}. All rights reserved.
          </p>
          <a
            href="/ad"
            className="font-sans text-[0.625rem] tracking-[0.2em] text-ivory/40 uppercase transition-colors hover:text-gold"
          >
            Announcements
          </a>
        </div>
      </footer>

      <PortfolioLightbox
        items={visible}
        index={open}
        onClose={() => setOpen(null)}
        onIndexChange={setOpen}
      />
    </div>
  );
}

export default PortfolioPage;
